import {defineMessages, FormattedMessage} from 'react-intl';
import {NavLink} from 'react-router-dom';

import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import mergeWith from 'lodash/mergeWith';
import uniqBy from 'lodash/uniqBy';

import {formatScheduledMessageFromApi} from '@hooks/services/communicator/useMessageTemplate';
import {formatForTravelSearch} from '@hooks/services/travel/utils';

import {
  ADDITIONAL_GROUPS,
  ADDITIONAL_RECIPIENTS,
  ADDITIONAL_SAVED_SEARCHES,
  GROUPS,
  MANAGED_RECIPIENTS,
  MESSAGE_TEMPLATE_ID,
  OPTIONAL_GROUPS,
  OPTIONAL_RECIPIENTS,
  OPTIONAL_SAVED_SEARCHES,
  OPTIONAL_SITES,
  PERSON_GROUP_IDS,
  PERSON_IDS,
  RECIPIENT_TYPE_ID_KEYS,
  RECIPIENTS,
  SAVED_SEARCH_IDS,
  SAVED_SEARCHES,
  SCHEDULED_MESSAGE_ID,
  SENDER_RECIPIENT_OPTIONS,
  SITES,
  TIERS,
} from '@constants/communicator';
import {FEATURES} from '@constants/features';
import {COMM_SUB_PATHS, ROUTE_URLS} from '@constants/routes';

import alsat from '@services/api/alsat';
import personGroups from '@services/api/person-groups';
import sites from '@services/api/sites';
import travelApi from '@services/api/travel';

import {featureToggle} from '@utils/featureToggle/featureToggle';

import communicatorApi from '../../../services/api/communicator';
import {notificationActions} from '../notification';
import {getSessionOrgId} from '../session/utils';
import * as types from './types';
import {
  getTopmostParentOrganization,
  normalizePrimaryContactFields,
  parseMessageTemplates,
  showConfirmDiscardMessage,
  splitRecipientsByCategory,
} from './utils';
import {convertPillsToVariablePlaceholdersInFormFields} from './variables-converter.utils';

const messages = defineMessages({
  sendMessageSuccess: {
    id: 'Redux.communicator.sendMessageSuccess',
    defaultMessage: 'Sending Message, for more details or to manage responses {clickLink}.',
  },
  clickLink: {
    id: 'Redux.communicator.clickLink',
    defaultMessage: 'click here',
  },
  sendMessageFailure: {
    id: 'Redux.communicator.sendMessageFailure',
    defaultMessage: 'Failed to send message',
  },
  createMessageDraftFailure: {
    id: 'Redux.communicator.createMessageDraftFailure',
    defaultMessage: 'Could not create message',
  },
  addRecipientFailure: {
    id: 'Redux.communicator.addRecipientFailure',
    defaultMessage: 'Unable to add recipient',
  },
  deleteRecipientFailure: {
    id: 'Redux.communicator.deleteRecipientFailure',
    defaultMessage: 'Unable to delete recipient',
  },
  getMessageFailure: {
    id: 'Redux.communicator.getMessageFailure',
    defaultMessage: 'Unable to get message',
  },
  getMessageRecipientsFailure: {
    id: 'Redux.communicator.getMessageRecipientsFailure',
    defaultMessage: 'Unable to get recipients',
  },
  deleteRecipientSuccess: {
    id: 'Redux.communicator.deleteRecipientSuccess',
    defaultMessage: 'Successfully removed recipient from draft',
  },
  addRecipientSuccess: {
    id: 'Redux.communicator.addRecipientSuccess',
    defaultMessage: 'Successfully added recipient to draft',
  },
  scheduleMessageSuccess: {
    id: 'Redux.communicator.scheduleMessageSuccess',
    defaultMessage: 'Successfully scheduled the message',
  },
  scheduleMessageFailure: {
    id: 'Redux.communicator.scheduleMessageFailure',
    defaultMessage: 'Failed to schedule the message',
  },
});

/**
 * Returns a promise.
 * If there is no message in progress, the promise resolves immediately.
 * If there is a message in progress, we show a confirmation dialog which asks the user to confirm that they want to discard their active message:
 *     If the user presses "OK", we resolve the promise.
 *     If the user presses "Cancel" we reject the promise.
 *
 *     Therefore, a call to `await dispatch(checkForUnsavedMessage());` in another action herein will block that action until a choice is made.
 *     If the user presses cancel, we reject the promise which throws an error and prevents the rest of the function from executing. The existing
 *     message is therefore preserved.
 * @returns {function(*, *): Promise<undefined>}
 */
function checkForUnsavedMessage() {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const composeMessageInProgress = getState().communicator.composeMessageInProgress;
      if (composeMessageInProgress) {
        showConfirmDiscardMessage({onOk: resolve, onCancel: reject});
      } else {
        resolve();
      }
    });
  };
}

export const initiateSitesComposeMessage = (
  filter,
  organizationId,
  includeSubOrganizations,
  personFilter,
  options = {},
) => {
  return async (dispatch) => {
    const initiationContext = {sitesPersonFilter: filter, ...(!isUndefined(personFilter) && {personFilter})};
    await dispatch(checkForUnsavedMessage());
    dispatch(endComposeMessage());
    dispatch({
      type: types.INITIATE_COMPOSE_MESSAGE,
      payload: {
        initialSubject: options.initialSubject,
        initialBody: options.initialBody,
      },
    });
    dispatch(createDraftMessageGeneric(initiationContext, organizationId, includeSubOrganizations));
  };
};

const fetchPersonRecipients = (managedRecipients) => async (dispatch) => {
  try {
    // grab only personIds from requiredRecipients and optionalRecipients
    // omit other properties since they're handled in the other recipientFetchers calls
    const personRecipientsOnly = {};

    if (managedRecipients.requiredRecipients?.personIds) {
      personRecipientsOnly.requiredRecipients = {
        personIds: managedRecipients.requiredRecipients.personIds,
      };
    }

    if (managedRecipients.optionalRecipients?.personIds) {
      personRecipientsOnly.optionalRecipients = {
        personIds: managedRecipients.optionalRecipients.personIds,
      };
    }
    const persons = await dispatch(getRecipientsAlsatFromCombinedFilter(personRecipientsOnly));
    return {type: RECIPIENT_TYPE_ID_KEYS.PERSON_IDS, data: persons};
  } catch (err) {
    return {type: RECIPIENT_TYPE_ID_KEYS.PERSON_IDS, data: []};
  }
};

const fetchSavedSearchRecipients =
  ({data, params}) =>
  async () => {
    try {
      const savedSearchesResponse = await alsat.getSavedSearches({
        params,
        data,
      });
      return {type: RECIPIENT_TYPE_ID_KEYS.SAVED_SEARCH_IDS, data: savedSearchesResponse?.data?.content};
    } catch (err) {
      return {type: RECIPIENT_TYPE_ID_KEYS.SAVED_SEARCH_IDS, data: []};
    }
  };

const fetchGroupRecipients =
  ({data, params}) =>
  async () => {
    const personGroupsResponse = await personGroups.searchPersonGroups({
      params,
      data,
    });
    return {type: RECIPIENT_TYPE_ID_KEYS.PERSON_GROUP_IDS, data: personGroupsResponse?.data?.content || []};
  };

const fetchSitesRecipients = (managedRecipients) => async (dispatch, getState) => {
  try {
    const siteIds = managedRecipients?.sites?.map((site) => site.siteId) || [];

    if (!siteIds.length) {
      return {type: RECIPIENT_TYPE_ID_KEYS.SITE_IDS, data: []};
    }

    const state = getState();
    const sessionOrgId = getSessionOrgId(state);
    const organizationIds = state?.communicator?.organizationIds ?? [sessionOrgId];

    const sitesResponse = await sites.getSitesSearchList({
      params: {page: 0, size: 500},
      data: {
        siteIds,
        organizationIds,
      },
    });

    const sitesList = sitesResponse?.data?.content || [];

    const modifiedSites = managedRecipients.sites.map((originalSite) => {
      const fetchedSite = sitesList.find((site) => site.id === originalSite.siteId);
      if (fetchedSite) {
        return {
          ...originalSite,
          id: fetchedSite.id,
          siteId: originalSite.siteId,
          name: fetchedSite.name,
          pocCount: fetchedSite.pocCount || 0,
          occupantCount: fetchedSite.occupantCount || 0,
          // Determine includePOC and includeOccupants based on siteType
          includePOC: originalSite.siteTypes?.includes('POC') ?? false,
          includeOccupants: originalSite.siteTypes?.includes('OCCUPANT') ?? false,
        };
      }
      return {
        ...originalSite,
        id: originalSite.siteId,
        siteId: originalSite.siteId,
      };
    });

    return {type: RECIPIENT_TYPE_ID_KEYS.SITE_IDS, data: modifiedSites};
  } catch (err) {
    return {type: RECIPIENT_TYPE_ID_KEYS.SITE_IDS, data: []};
  }
};

/**
 * Resolves id-only managed recipients ({personIds, personGroupIds, savedSearchIds, sites}) into full recipient objects,
 * keyed by recipient type. `fetchPeople` receives the person ids and performs the people lookup.
 */
const fetchRecipientsByType = (managedRecipients, fetchPeople) => {
  return async (dispatch) => {
    if (!managedRecipients) return {};

    const fetches = [];
    const commonQueryParams = {
      page: 0,
      size: 500,
    };

    const recipientFetchers = [
      {
        key: RECIPIENT_TYPE_ID_KEYS.PERSON_IDS,
        fetch: fetchPeople,
      },
      {
        key: RECIPIENT_TYPE_ID_KEYS.SAVED_SEARCH_IDS,
        fetch: (savedSearchIds) =>
          fetchSavedSearchRecipients({
            data: {savedSearchIds},
            params: commonQueryParams,
          }),
      },
      {
        key: RECIPIENT_TYPE_ID_KEYS.PERSON_GROUP_IDS,
        fetch: (ids) => fetchGroupRecipients({data: {ids}, params: commonQueryParams}),
      },
    ];

    recipientFetchers.forEach(({key, fetch}) => {
      const ids = managedRecipients?.[key];
      if (ids?.length) {
        fetches.push(dispatch(fetch(ids)));
      }
    });

    // Handle sites separately by calling fetchSitesRecipients if sites exist
    if (managedRecipients?.sites?.length) {
      fetches.push(dispatch(fetchSitesRecipients(managedRecipients)));
    }

    const allRecipients = await Promise.all(fetches);
    const recipientsByType = {};
    allRecipients.forEach(({type, data}) => (recipientsByType[type] = data));
    return recipientsByType;
  };
};

export const getRecipientsFromMessage = (managedRecipients, isOptional = false) => {
  const personsPayload = isOptional ? {optionalRecipients: managedRecipients} : {requiredRecipients: managedRecipients};

  return fetchRecipientsByType(managedRecipients, () => fetchPersonRecipients(personsPayload));
};

export const initiateEditSchedule = (message, viewOnly) => {
  return async (dispatch, getState) => {
    const state = getState();
    const organizationId = state?.session.currentPermissions?.orgId;

    dispatch({type: types.GET_SCHEDULED_MESSAGE_BY_ID_REQUEST});
    try {
      const res = await communicatorApi.getScheduledMessageById({
        pathParams: {organizationId, scheduledMessageId: message.id},
      });
      const {data} = res;
      let managedRecipients = data?.[MANAGED_RECIPIENTS];
      if (viewOnly) return;

      const isQuotaTiersEnabled = featureToggle(FEATURES.HZMN_350);

      //If there are no managed recipients, we check the first quota tier and apply the recipients in the first tier to the "managed recipients" field
      //If HZMN_350 is enabled (quota tiers), then we don't use managed recipients field, since each tier keeps track of its own managed recipients
      if (!managedRecipients) {
        managedRecipients = isQuotaTiersEnabled
          ? {}
          : data?.config?.deliveryRules?.quotaRule?.tiers?.[0]?.managedRecipients;
      }
      //The tiers only have recipient ids here, ComposeMessageDrawer looks the recipients up with useTemplateTierRecipients
      const tiers = data?.config?.deliveryRules?.quotaRule?.tiers;

      let recipientsByType;
      if (!isEmpty(managedRecipients)) {
        recipientsByType = await dispatch(getRecipientsFromMessage(managedRecipients));
      }

      const scheduledMessage = formatScheduledMessageFromApi({
        ...message,
        ...data,
        ...(recipientsByType && {[MANAGED_RECIPIENTS]: recipientsByType}),
        ...(tiers && {[TIERS]: tiers}),
      });

      dispatch({
        type: types.GET_SCHEDULED_MESSAGE_BY_ID_SUCCESS,
        payload: {
          scheduledMessage,
          organizationIds: [organizationId],
        },
      });
    } catch (err) {
      console.error('initiateEditSchedule error', err);
      dispatch({type: types.GET_SCHEDULED_MESSAGE_BY_ID_FAILURE});
    }
  };
};

const getSitesForMessage = (sitesFilter) => {
  return async (dispatch, getState, {sites: sitesApi}) => {
    const {siteFilter, includePOC, includeOccupants} = sitesFilter;
    const state = getState();
    const locale = state?.localization?.locale;

    return sitesApi
      .getSitesSearchList({params: {locale, page: 0, size: 10000}, data: siteFilter})
      .then((res) =>
        (res?.data?.content || []).map((site) => ({
          id: site.id,
          siteId: site.id,
          name: site.name,
          pocCount: site.pocCount || 0,
          occupantCount: site.occupantCount || 0,
          includePOC,
          includeOccupants,
        })),
      )
      .catch((err) => {
        console.error('error getting sites for message', err, sitesFilter);
        dispatch(
          notificationActions.showErrorNotification(<FormattedMessage {...messages.getMessageRecipientsFailure} />),
        );
        return [];
      });
  };
};

/**
 * Initiates compose message workflow using ALSAT rollup data for recipients.
 * Fetches affected persons from ALSAT location rollup searches and site objects
 * (for POC/occupant recipients), then initiates message composition with enhanced recipients.
 *
 * @param {Object} alsatFilter - The ALSAT filter configuration
 * @param {Array} alsatFilter.dateRanges - Array of date range objects with start/end dates
 * @param {Array} alsatFilter.polygons - Array of polygon geometries for location filtering
 * @param {Array} alsatFilter.organizationIds - Array of organization IDs to filter by
 * @param {boolean} alsatFilter.includeSubOrganizations - Whether to include sub-organizations
 * @param {Array} alsatFilter.personLocationTypes - Array of person location types to include
 * @param {Object} [options={}] - Additional options for message composition
 * @param {Object} [options.sitesFilter] - Polygon filter for site-based recipients (POC/occupants)
 * @param {Array} [options.sites] - Explicit site objects to include as recipients (overrides sitesFilter result)
 * @param {Array} [options.groups] - Array of group objects to include as recipients
 * @returns {Function} Redux thunk function that performs the async operation
 * @throws {Error} Throws error if ALSAT API call fails
 */
export const initiateComposeMessageWithAlsatRollup = (alsatFilter, options) => {
  return async (dispatch, getState, {alsat}) => {
    const alsatPromises = [];

    // Only fetch from ALSAT if there are location types to search for.
    if (alsatFilter?.personLocationTypes?.length) {
      const state = getState();
      const locale = state?.localization?.locale;
      const params = {
        includeDetails: true,
        includeItineraries: false,
        locale,
        page: 0,
        size: 10000,
      };
      const config = {
        data: {...alsatFilter},
        params,
      };
      alsatPromises.push(
        alsat
          .getAlsatPersonsLocationsRollupSearchesConfigPattern(config)
          .then((res) => res?.data?.content?.map((recipient) => recipient.person) || [])
          .catch((error) => {
            throw new Error(error);
          }),
      );
    }

    const sitesFetch = options?.sitesFilter ? dispatch(getSitesForMessage(options.sitesFilter)) : Promise.resolve([]);

    const [alsatResults, fetchedSites] = await Promise.all([Promise.all(alsatPromises), sitesFetch]);
    const recipients = uniqBy(alsatResults.flat().filter(Boolean), 'id');

    dispatch(
      initiateComposeMessageWithEnhancedRecipients({
        recipients,
        sites: options?.sites ?? fetchedSites,
        groups: options?.groups,
        organizationIds: options?.organizationIds,
        templateToSend: options?.templateToSend,
        initialSubject: options?.initialSubject,
        initialBody: options?.initialBody,
      }),
    );
  };
};

export const initiateComposeMessageWithPeopleInGroup = (groupId, filter = {}) => {
  return (dispatch, getState) => {
    const state = getState();
    const locale = state?.localization?.locale;

    const params = {
      locale,
      page: 0,
      size: 10000,
    };

    return personGroups
      .searchPersonGroupPeople({
        pathParams: {
          groupId,
        },
        params,
        data: filter,
      })
      .then((res) => {
        const recipients = res?.data?.content;
        dispatch(
          initiateComposeMessageWithEnhancedRecipients({
            recipients,
            organizationIds: filter?.organizationIds,
          }),
        );
      })
      .catch((err) => {
        console.error('err', err);
        dispatch(
          notificationActions.showErrorNotification(<FormattedMessage {...messages.createMessageDraftFailure} />),
        );
      });
  };
};

export const initiateComposeMessageWithRecipientsAndPeopleAtSites = (filter) => {
  return async (dispatch) => {
    const {siteFilter, personIds, organizationIds, personLocationType} = filter;
    const promises = [];

    if (siteFilter) {
      promises.push(
        dispatch(
          getPeopleAtSitesForMessage({
            siteFilter,
            type: personLocationType,
            organizationIds,
          }),
        ),
      );
    }
    if (personIds) {
      promises.push(
        dispatch(
          getRecipientsAlsat({
            personIds,
            organizationIds,
          }),
        ),
      );
    }
    Promise.all(promises)
      .then((res) => {
        const allRecipients = res.flat().filter(Boolean);
        const recipients = uniqBy(allRecipients, 'id');

        return dispatch(
          initiateComposeMessageWithEnhancedRecipients({
            recipients,
            organizationIds: filter?.organizationIds,
          }),
        );
      })
      .catch((err) => {
        console.error('err', err);
      });
  };
};

export const getPeopleAtSitesForMessage = (filter) => {
  return async (_dispatch, getState, {sites}) => {
    const state = getState();
    const locale = state?.localization?.locale;

    const params = {
      locale,
      page: 0,
      size: 10000,
    };
    return sites
      .getPeopleAtSites({params, data: filter})
      .then((res) => {
        return res?.data?.content.map(normalizePrimaryContactFields);
      })
      .catch((err) => {
        console.error('error getting people at sites', err, filter);
      });
  };
};

export const initiateComposeMessageWithPeopleAtSites = (filter) => {
  return async (dispatch) => {
    return dispatch(getPeopleAtSitesForMessage(filter)).then((recipients) => {
      dispatch(
        initiateComposeMessageWithEnhancedRecipients({
          recipients,
          organizationIds: filter?.organizationIds,
        }),
      );
    });
  };
};

export const getPeopleForTravelSearchMessage = (formattedFilter) => {
  return async (_dispatch, getState) => {
    const locale = getState()?.localization?.locale;

    const params = {
      locale,
      page: 0,
      size: 10000,
    };

    return travelApi
      .getTravelPersonsList({params, data: formattedFilter})
      .then((res) => {
        return res?.data?.content;
      })
      .catch((err) => {
        console.error('error getting people for travel search message', err, formattedFilter);
      });
  };
};

export const initiateComposeMessageWithTravelSearchFilter = (filter) => {
  return async (dispatch, getState) => {
    const sessionOrgId = getSessionOrgId(getState());
    const formattedFilter = formatForTravelSearch(filter, sessionOrgId);

    return dispatch(getPeopleForTravelSearchMessage(formattedFilter)).then((recipients) => {
      dispatch(
        initiateComposeMessageWithEnhancedRecipients({
          recipients,
          organizationIds: formattedFilter.organizationIds,
        }),
      );
    });
  };
};

export const getRecipientsAlsat = (payload) => {
  return async (dispatch, getState, {alsat}) => {
    const state = getState();
    const locale = state?.localization?.locale;

    dispatch({type: types.GET_ALSAT_MESSAGE_RECIPIENTS_REQUEST});
    const params = {
      includeDetails: true,
      includeItineraries: false,
      locale,
      page: 0,
      size: 10000,
    };

    return alsat
      .getAlsatPersonsListConfigPattern({data: payload, params})
      .then((res) => {
        const recipientsData = res?.data;
        const recipients = recipientsData?.content;
        const {pageable} = recipientsData;

        const formattedRecipients = recipients.map((person) => {
          return {
            ...person,
            email: person.email,
          };
        });

        dispatch({
          type: types.GET_ALSAT_MESSAGE_RECIPIENTS_SUCCESS,
          payload: {
            recipients: formattedRecipients,
            pageable: {
              ...pageable,
              pageNumber: params?.page ?? 0,
              pageSize: params?.size,
            },
          },
        });
        return formattedRecipients;
      })

      .catch((err) => {
        dispatch({type: types.GET_ALL_SENDER_RECIPIENTS_FAILURE});
        console.error('err', err);
      });
  };
};

export const getRecipientsAlsatFromCombinedFilter = ({requiredRecipients, optionalRecipients}) => {
  return async (dispatch, getState, {alsat}) => {
    dispatch({type: types.GET_ALSAT_MESSAGE_RECIPIENTS_REQUEST});

    const state = getState();
    const locale = state?.localization?.locale;

    const filterParams = mergeWith(
      {},
      // add requiredRecipients/optionalRecipients or empty if null/undefined
      requiredRecipients ?? {},
      optionalRecipients ?? {},
      (targetVal, sourceVal) => {
        if (targetVal?.length && sourceVal?.length) {
          return targetVal.concat(sourceVal);
        }
      },
    );

    const commonQueryParams = {
      page: 0,
      size: 500,
      locale,
      filterParams,
    };

    return alsat
      .getAlsatPersonsListConfigPattern({
        params: {
          page: commonQueryParams.page,
          size: commonQueryParams.size,
          locale: commonQueryParams.locale,
        },
        data: commonQueryParams.filterParams,
      })
      .then((res) => {
        const recipientsData = res?.data;
        const {pageable} = recipientsData;
        const recipients = recipientsData?.content;

        const formattedRecipients = recipients.map((person) => {
          return {
            ...person,
            email: person.email,
          };
        });

        const splitRecipients = splitRecipientsByCategory(formattedRecipients, requiredRecipients, optionalRecipients);

        dispatch({
          type: types.GET_ALSAT_MESSAGE_RECIPIENTS_SUCCESS,
          payload: {
            recipients: splitRecipients.requiredRecipients,
            optionalRecipients: splitRecipients.optionalRecipients,
            pageable: {
              ...pageable,
              pageNumber: commonQueryParams?.page ?? 0,
              pageSize: commonQueryParams?.size,
            },
          },
        });
        return formattedRecipients;
      })
      .catch(() => {
        dispatch({type: types.GET_ALSAT_MESSAGE_RECIPIENTS_FAILURE});
        dispatch(
          notificationActions.showErrorNotification(<FormattedMessage {...messages.getMessageRecipientsFailure} />),
        );
      });
  };
};

export const initiateViewScheduledMessage = (message) => {
  const viewOnly = true;

  return async (dispatch) => {
    await dispatch(checkForUnsavedMessage());
    dispatch(endComposeMessage());
    dispatch(initiateEditSchedule(message, viewOnly));
  };
};

export const initiateEditScheduleComposeMessage = (message, isEditingAllowSend) => {
  return async (dispatch) => {
    try {
      await dispatch(checkForUnsavedMessage());
      dispatch(endComposeMessage());
      dispatch({
        type: types.INITIATE_COMPOSE_MESSAGE,
        payload: {
          numPersons: message.recipientCount,
          isEditingAllowSend,
        },
      });

      await dispatch(initiateEditSchedule(message));
    } catch (error) {
      console.error('initiateEditScheduleComposeMessage err:', error);
      dispatch({type: types.CREATE_DRAFT_MESSAGE_FAILURE});
    }
  };
};

export const initiateComposeMessageGeneric = (filter, organizationId, options = {}) => {
  return async (dispatch) => {
    await dispatch(checkForUnsavedMessage());
    dispatch(endComposeMessage());
    dispatch({
      type: types.INITIATE_COMPOSE_MESSAGE,
      payload: {
        templateToSend: options.templateToSend,
      },
    });

    // The organizationId argument value will always be a top level client id;
    // however, a user may NOT have access to the top level client.
    // If we pass that client id for that user the POST /draft call will fail.
    // So we will get the orgId to pass from the personFilter object, which will only have the
    // ids of the orgs to which the user has access.
    const orgIdForCompose = filter?.personFilter?.organizationIds?.[0] || organizationId;

    dispatch(
      createDraftMessageGeneric(
        {
          sitesPersonFilter: filter?.sitesPersonFilter,
          personFilter: filter?.personFilter,
          personGroupPersonFilter: filter?.personGroupPersonFilter,
          templateToSend: options.templateToSend,
        },
        orgIdForCompose,
      ),
    );
  };
};

export const initiateComposeMessageWithEnhancedRecipients = (options = {}) => {
  return async (dispatch, getState) => {
    await dispatch(checkForUnsavedMessage());
    dispatch(endComposeMessage());

    const {recipients, groups, sites, organizationIds, savedSearches, initialSubject, initialBody} = options;

    const organizationDictionary = getState().session.organizationsDictionary;
    // All organizations in organizationIds are by definition in the same client hierarchy,
    // otherwise the compose message button would be disabled, and we would never arrive to this point in the code.

    // Therefore, we can search for the topmost parent organization using any of the organizationIds.
    // We pick the first one for convenience’s sake.
    const firstOrganizationInOrgFilter = organizationIds?.[0];
    const topmostParentOrganizationId = getTopmostParentOrganization(
      organizationDictionary,
      firstOrganizationInOrgFilter,
    )?.orgId;

    const sessionOrg = getSessionOrgId(getState());

    dispatch({
      type: types.INITIATE_COMPOSE_MESSAGE_ENHANCED_RECIPIENTS,
      payload: {
        organizationIds: isNil(topmostParentOrganizationId) ? [sessionOrg] : [topmostParentOrganizationId],
        recipients: recipients ?? [],
        groups: groups ?? [],
        sites: sites ?? [],
        savedSearches: savedSearches ?? [],
        templateToSend: options?.templateToSend,
        initialSubject: initialSubject ?? undefined,
        initialBody: initialBody ?? undefined,
      },
    });
  };
};

export const createDraftMessageOpen = (body) => {
  return (dispatch, _getState, {communicator}) => {
    dispatch({type: types.CREATE_DRAFT_MESSAGE_REQUEST});
    return communicator
      .createDraftMessageOpen(body)
      .then((res) => {
        const {data} = res;
        const {author, id, initiationContext} = data;
        dispatch({
          payload: {
            author,
            id,
            initiationContext,
            organizationIds: [body?.organizationId],
          },
          type: types.CREATE_DRAFT_MESSAGE_SUCCESS,
        });
        dispatch(getMessageRecipients(id));
      })
      .catch(() => {
        dispatch({type: types.CREATE_DRAFT_MESSAGE_FAILURE});
        dispatch(endComposeMessage());
        dispatch(
          notificationActions.showErrorNotification(<FormattedMessage {...messages.createMessageDraftFailure} />),
        );
      });
  };
};

export const createDraftMessageGeneric = (filter, organizationId, includeSubOrganizations) => {
  return (dispatch, getState, {communicator}) => {
    dispatch({type: types.CREATE_DRAFT_MESSAGE_REQUEST});

    return communicator
      .createDraftMessage({
        organizationId,
        sitesPersonFilter: filter?.sitesPersonFilter,
        filter: filter?.personFilter,
        personGroupPersonFilter: filter?.personGroupPersonFilter,
      })
      .then((res) => {
        const {data} = res;
        const {author, id, initiationContext} = data;

        dispatch({
          payload: {
            author,
            id,
            initiationContext,
            organizationIds: [organizationId],
            includeSubOrganizations,
            hasEnhancedRecipientManagement: !!filter.templateToSend,
          },
          type: types.CREATE_DRAFT_MESSAGE_SUCCESS,
        });
        if (!filter?.templateToSend) {
          dispatch(getMessageRecipients(id));
        }
      })
      .catch(() => {
        dispatch({type: types.CREATE_DRAFT_MESSAGE_FAILURE});
        dispatch(endComposeMessage());
        dispatch(
          notificationActions.showErrorNotification(<FormattedMessage {...messages.createMessageDraftFailure} />),
        );
      });
  };
};

export const createDraftMessage = (filter, personId, personOrgId, alertId, sitesPersonFilter) => {
  return (dispatch, getState, {communicator}) => {
    dispatch({type: types.CREATE_DRAFT_MESSAGE_REQUEST});
    const organizationIds = filter?.organizationIds || sitesPersonFilter?.siteFilter?.organizationIds;
    const includeSubOrganizations = filter?.includeSubOrganizations;

    // org ID for sending message to individual person OR session org
    const organizationId = organizationIds?.length
      ? organizationIds[0] // grab first organizationId if passed in the filter
      : (personOrgId ?? getSessionOrgId(getState()));

    return communicator
      .createDraftMessage({organizationId, includeSubOrganizations, filter, personId, alertId, sitesPersonFilter})
      .then((res) => {
        const {data} = res;

        const {author, id, initiationContext, fromEmail} = data;
        dispatch({
          payload: {
            author,
            id,
            initiationContext,
            organizationIds: organizationIds || [organizationId],
            includeSubOrganizations: includeSubOrganizations,
            fromEmail,
          },
          type: types.CREATE_DRAFT_MESSAGE_SUCCESS,
        });

        dispatch(getMessageRecipients(id));
      })
      .catch(() => {
        dispatch({type: types.CREATE_DRAFT_MESSAGE_FAILURE});
        dispatch(endComposeMessage());
        dispatch(
          notificationActions.showErrorNotification(<FormattedMessage {...messages.createMessageDraftFailure} />),
        );
      });
  };
};

export const initiateComposeMessageWithAlsatFilter = (filter, organizationIds) => {
  return async (dispatch) => {
    const persons = await dispatch(getRecipientsAlsat(filter));
    dispatch(initiateComposeMessageWithEnhancedRecipients({recipients: persons, organizationIds: organizationIds}));
  };
};

export const initiateComposeMessageWithSites = (sites, organizationIds) => {
  return async (dispatch) => {
    await dispatch(initiateComposeMessageWithEnhancedRecipients({sites: sites, organizationIds: organizationIds}));
  };
};

export const getMessageRecipients = (messageId, {pageNumber, pageSize = 25} = {}) => {
  return (dispatch, _getState, {communicator}) => {
    dispatch({type: types.GET_MESSAGE_RECIPIENTS_REQUEST});

    return communicator
      .getMessageRecipients(messageId, pageNumber, pageSize)
      .then((res) => {
        const {content, pageable} = res.data;
        pageable.totalElements = res.data.totalElements;
        pageable.sort = res.data.sort;
        const formattedContent = content.map((person) => {
          return {
            ...person,
            email: person.emailAddress,
          };
        });
        dispatch({
          payload: {
            content: formattedContent,
            pageable: {
              ...pageable,
              pageNumber: pageNumber ?? 0,
              pageSize,
            },
          },
          type: types.GET_MESSAGE_RECIPIENTS_SUCCESS,
        });
      })
      .catch(() => {
        dispatch({type: types.GET_MESSAGE_RECIPIENTS_FAILURE});
        dispatch(
          notificationActions.showErrorNotification(<FormattedMessage {...messages.getMessageRecipientsFailure} />),
        );
      });
  };
};

export const getMessageTemplates = (locale) => {
  return (dispatch, _getState, {communicator}) => {
    dispatch({type: types.GET_MESSAGE_TEMPLATES_REQUEST});

    return communicator
      .getMessageTemplates(locale)
      .then((res) => {
        dispatch({
          payload: {
            // we want to return parsed templates that have expected content properties (name, subject and richText)
            messageTemplates: parseMessageTemplates(res.data),
          },
          type: types.GET_MESSAGE_TEMPLATES_SUCCESS,
        });
      })
      .catch(() => {
        dispatch({type: types.GET_MESSAGE_TEMPLATES_FAILURE});
      });
  };
};

export const scheduleMessage = (message) => {
  return (dispatch, getState, {communicator}) => {
    dispatch({type: types.SCHEDULE_MESSAGE_REQUEST});
    const state = getState();
    const sessionOrgId = getSessionOrgId(state);

    const processedMessage = {
      ...message,
      config: convertPillsToVariablePlaceholdersInFormFields(message.config),
    };

    const body = {
      pathParams: {organizationId: message.config.organizationId ?? sessionOrgId},
      data: processedMessage,
    };

    return communicator
      .scheduleMessage(body)
      .then(() => {
        dispatch({type: types.SCHEDULE_MESSAGE_SUCCESS});
        dispatch(endComposeMessage());
        dispatch(
          notificationActions.showSuccessNotification(<FormattedMessage {...messages.scheduleMessageSuccess} />),
        );
      })
      .catch(() => {
        dispatch({type: types.SCHEDULE_MESSAGE_FAILURE});
        dispatch(endComposeMessage());
        dispatch(notificationActions.showErrorNotification(<FormattedMessage {...messages.scheduleMessageFailure} />));
      });
  };
};

export const sendMessage = (message) => {
  return (dispatch, _getState, {communicator}) => {
    dispatch({type: types.SEND_MESSAGE_REQUEST});

    const processedMessage = {
      ...message,
      messageConfig: convertPillsToVariablePlaceholdersInFormFields(message.messageConfig),
    };

    return communicator
      .sendMessage(processedMessage)
      .then((_res) => {
        const messageId = message.messageId;
        dispatch({type: types.SEND_MESSAGE_SUCCESS});
        dispatch(endComposeMessage());
        dispatch(
          notificationActions.showSuccessNotification(
            <FormattedMessage
              {...messages.sendMessageSuccess}
              values={{
                clickLink: (
                  <NavLink
                    to={`${ROUTE_URLS.COMMUNICATION}/${COMM_SUB_PATHS.MASS_NOTIFICATION}/${COMM_SUB_PATHS.SENT}/${COMM_SUB_PATHS.SUMMARY}/${messageId}`}
                  >
                    <FormattedMessage {...messages.clickLink} />
                  </NavLink>
                ),
              }}
            />,
          ),
        );
      })
      .catch(() => {
        dispatch({type: types.SEND_MESSAGE_FAILURE});
        dispatch(endComposeMessage());
        dispatch(notificationActions.showErrorNotification(<FormattedMessage {...messages.sendMessageFailure} />));
      });
  };
};

export const sendDraftMessage = ({
  formats,
  conferenceBridge,
  statusConfirmationOptions,
  survey,
  messageLanguage,
  attachmentIds,
}) => {
  return (dispatch, _getState, {communicator}) => {
    dispatch({type: types.SEND_MESSAGE_REQUEST});

    const processedFormats = convertPillsToVariablePlaceholdersInFormFields({formats}).formats;

    return communicator
      .updateMessage({
        params: {formats: processedFormats, conferenceBridge, statusConfirmationOptions, survey, attachmentIds},
      })
      .then((res) => {
        const messageId = res.data.id;

        return communicator
          .sendDraftMessage({data: {messageLanguage}})
          .then(() => {
            dispatch({type: types.SEND_MESSAGE_SUCCESS});
            dispatch(endComposeMessage());
            dispatch(
              notificationActions.showSuccessNotification(
                <FormattedMessage
                  {...messages.sendMessageSuccess}
                  values={{
                    clickLink: (
                      <NavLink
                        to={`${ROUTE_URLS.COMMUNICATION}/${COMM_SUB_PATHS.MASS_NOTIFICATION}/${COMM_SUB_PATHS.SENT}/${COMM_SUB_PATHS.SUMMARY}/${messageId}`}
                      >
                        <FormattedMessage {...messages.clickLink} />
                      </NavLink>
                    ),
                  }}
                />,
              ),
            );
          })
          .catch(() => {
            dispatch({type: types.SEND_MESSAGE_FAILURE});
            dispatch(endComposeMessage());
            dispatch(notificationActions.showErrorNotification(<FormattedMessage {...messages.sendMessageFailure} />));
          });
      })
      .catch(() => {
        dispatch({type: types.SEND_MESSAGE_FAILURE});
        dispatch(endComposeMessage());
        dispatch(notificationActions.showErrorNotification(<FormattedMessage {...messages.sendMessageFailure} />));
      });
  };
};

export const endComposeMessage = () => {
  return (dispatch, _getState) => {
    dispatch({type: types.END_COMPOSE_MESSAGE});
  };
};

export const addRecipient = (recipientId) => {
  return (dispatch, getState, {communicator}) => {
    const communicatorState = getState()?.communicator;
    const {pageable, messageId} = communicatorState;

    dispatch({type: types.ADD_RECIPIENT_REQUEST});
    return communicator
      .addRecipient(recipientId)
      .then(() => {
        dispatch({
          type: types.ADD_RECIPIENT_SUCCESS,
          payload: recipientId,
        });
        dispatch(getMessageRecipients(messageId, pageable));
        dispatch(notificationActions.showSuccessNotification(<FormattedMessage {...messages.addRecipientSuccess} />));
      })
      .catch(() => {
        dispatch({type: types.ADD_RECIPIENT_FAILURE});
        dispatch(notificationActions.showErrorNotification(<FormattedMessage {...messages.addRecipientFailure} />));
      });
  };
};

export const addAllRecipients = (recipients = []) => {
  return (dispatch) => {
    return dispatch({type: types.ADD_ALL_RECIPIENTS, payload: recipients});
  };
};

export const addAllGroups = (groups = []) => {
  return (dispatch) => {
    return dispatch({type: types.ADD_ALL_GROUPS, payload: groups});
  };
};

export const addAllOptionalGroups = (groups = []) => {
  return (dispatch) => {
    return dispatch({type: types.ADD_ALL_OPTIONAL_GROUPS, payload: groups});
  };
};

export const addAllSavedSearches = (savedSearches = []) => {
  return (dispatch) => {
    return dispatch({type: types.ADD_ALL_SAVED_SEARCHES, payload: savedSearches});
  };
};

export const addAllOptionalSavedSearches = (savedSearches = []) => {
  return (dispatch) => {
    return dispatch({type: types.ADD_ALL_OPTIONAL_SAVED_SEARCHES, payload: savedSearches});
  };
};

export const addAllSites = (sites = []) => {
  return (dispatch) => {
    return dispatch({type: types.ADD_ALL_SITES, payload: sites});
  };
};

export const addAllOptionalRecipients = (optionalRecipients = []) => {
  return (dispatch) => {
    return dispatch({type: types.ADD_ALL_OPTIONAL_RECIPIENTS, payload: optionalRecipients});
  };
};

export const addAllOptionalSites = (optionalSites = []) => {
  return (dispatch) => {
    return dispatch({type: types.ADD_ALL_OPTIONAL_SITES, payload: optionalSites});
  };
};

export const addAllAdditionalRecipients = (additionalRecipients = []) => {
  return (dispatch) => {
    return dispatch({type: types.ADD_ALL_ADDITIONAL_RECIPIENTS, payload: additionalRecipients});
  };
};

export const addAllAdditionalGroups = (additionalGroups = []) => {
  return (dispatch) => {
    return dispatch({type: types.ADD_ALL_ADDITIONAL_GROUPS, payload: additionalGroups});
  };
};

export const addAllAdditionalSavedSearches = (additionalSavedSearches = []) => {
  return (dispatch) => {
    return dispatch({type: types.ADD_ALL_ADDITIONAL_SAVED_SEARCHES, payload: additionalSavedSearches});
  };
};

export const addAllAdditionalSites = (additionalSites = []) => {
  return (dispatch) => {
    return dispatch({type: types.ADD_ALL_ADDITIONAL_SITES, payload: additionalSites});
  };
};

const buildSenderRecipientPayload = (message, organizationId, requiredRecipients) => {
  const isScheduledMessage = message?.type || message?.config;
  const messageIdKey = isScheduledMessage ? SCHEDULED_MESSAGE_ID : MESSAGE_TEMPLATE_ID;

  return {
    pathParams: {organizationId},
    params: {
      [messageIdKey]: message.id,
      requiredRecipients,
      page: 0,
      size: 10000,
    },
  };
};

const fetchManagedRecipients = (managedRecipients) => {
  return (dispatch, getState, {alsat}) => {
    const state = getState();
    const locale = state?.localization?.locale;

    const commonQueryParams = {
      page: 0,
      size: 500,
      locale,
      filterParams: managedRecipients,
    };

    dispatch({type: types.GET_MANAGED_RECIPIENTS_REQUEST});

    return alsat
      .getAlsatPersonsListConfigPattern({
        params: {
          page: commonQueryParams.page,
          size: commonQueryParams.size,
          locale: commonQueryParams.locale,
        },
        data: commonQueryParams.filterParams,
      })
      .then((res) => {
        const recipientsData = res?.data;
        const recipients = recipientsData?.content;

        const formattedRecipients = recipients.map((person) => {
          return {
            ...person,
            email: person.email,
          };
        });
        dispatch({
          type: types.GET_MANAGED_RECIPIENTS_SUCCESS,
          payload: formattedRecipients,
        });
      })
      .catch(() => {
        dispatch({type: types.GET_MANAGED_RECIPIENTS_FAILURE});
      });
  };
};

export const getSenderRecipients = (message) => {
  return async (dispatch, getState, {communicator}) => {
    const senderRecipientOptions = message?.senderRecipientOptions;

    const isQuotaTiersEnabled = featureToggle(FEATURES.HZMN_350);
    const quotaManagedRecipients =
      message?.messageConfig?.deliveryRules?.quotaRule?.tiers?.[0]?.managedRecipients ||
      message?.config?.deliveryRules?.quotaRule?.tiers?.[0]?.managedRecipients;

    const hasRequiredRecipients = !!senderRecipientOptions?.requiredRecipients;
    const hasOptionalRecipients = !!senderRecipientOptions?.optionalRecipients;
    const hasAdditionalRecipients = !!senderRecipientOptions?.additionalRecipients;
    const state = getState();
    const communicatorState = state?.communicator;
    const organizationId = state?.session.currentPermissions?.orgId;
    const {type} = message;

    if (
      message?.name &&
      message.managedRecipients &&
      [SENDER_RECIPIENT_OPTIONS.CAN_ADD_SPECIFIC_RECIPIENTS, SENDER_RECIPIENT_OPTIONS.CAN_ADD_RECIPIENTS].includes(
        type,
      ) &&
      //This prevents quota recipients from populated the regular managed recipients by accident when loading a template
      !(isQuotaTiersEnabled && quotaManagedRecipients)
    ) {
      dispatch(fetchManagedRecipients(message.managedRecipients));
    }

    // when preserveRecipients is true, we need to keep the existing recipients and not turn hasEnhancedRecipientManagement.
    // However, when 'shouldFetchTemplateRecipients' is set (HZMN_233: user chose to include template recipients),
    // bypass the preserve check so the template's recipients are fetched from the API.

    const senderRecipientsPeoplePayload = {
      [RECIPIENTS]: {content: communicatorState?.recipients || [], pageable: communicatorState?.pageable},
      [GROUPS]: {content: communicatorState?.groups || []},
      [OPTIONAL_GROUPS]: {content: communicatorState?.optionalGroups || []},
      [SAVED_SEARCHES]: {content: communicatorState?.savedSearches || []},
      [OPTIONAL_SAVED_SEARCHES]: {content: communicatorState?.optionalSavedSearches || []},
      [SITES]: {content: communicatorState?.sites || []},
      [OPTIONAL_SITES]: {content: communicatorState?.optionalSites || []},
    };
    if (communicatorState?.preserveRecipients && !communicatorState?.shouldFetchTemplateRecipients) {
      dispatch({
        type: types.GET_ALL_SENDER_RECIPIENTS_SUCCESS,
        payload: {
          ...senderRecipientsPeoplePayload,
          [OPTIONAL_RECIPIENTS]: {content: communicatorState?.optionalRecipients || []},
        },
      });

      // 'CAN_ADD_SPECIFIC_RECIPIENTS' templates have an optional people pool that must be
      // fetched from the API - the bypass preserves required recipients (a list of people from a non-template source such as
      // person search, sites, alert exposure list etc)
      // but 'optionalRecipients' in redux were reset above to the page's empty optional list.
      // Without this fetch the 'SelectGroupInput' renders 'null' (it hides itself when options === []).
      if (hasOptionalRecipients && !isEmpty(senderRecipientOptions?.optionalRecipients?.[PERSON_IDS])) {
        const fetchPayload = buildSenderRecipientPayload(message, organizationId, false);
        communicator
          .getSenderRecipientsPeople(fetchPayload)
          .then((res) => {
            dispatch({
              type: types.GET_ALL_SENDER_RECIPIENTS_SUCCESS,
              payload: {
                ...senderRecipientsPeoplePayload,
                [OPTIONAL_RECIPIENTS]: res.data,
              },
            });
          })
          .catch(() => {});
      }

      return;
    }

    // Reset 'shouldFetchTemplateRecipients' flag now that we're proceeding with fetch
    if (communicatorState?.shouldFetchTemplateRecipients) {
      dispatch({type: types.SET_SHOULD_FETCH_TEMPLATE_RECIPIENTS, payload: false});
    }

    dispatch({type: types.INITIATE_SENDER_RECIPIENTS_FETCH});
    if (quotaManagedRecipients) {
      try {
        let recipientsByType = {};

        // Quota tier recipients are resolved separately by the useTemplateTierRecipients hook (via getTierRecipients),
        // which avoids dispatching the GET_ALSAT_MESSAGE_RECIPIENTS_* actions that fetchPersonRecipients would otherwise
        // trigger here and that would clobber the shared "manage recipients" state.
        if (!isQuotaTiersEnabled) {
          recipientsByType = await dispatch(getRecipientsFromMessage(quotaManagedRecipients));
        }
        dispatch({
          type: types.GET_ALL_SENDER_RECIPIENTS_SUCCESS,
          payload: {
            [RECIPIENTS]: {content: recipientsByType?.[PERSON_IDS] || [], pageable: communicatorState?.pageable},
            [OPTIONAL_RECIPIENTS]: {content: []},
            [GROUPS]: {content: recipientsByType?.[PERSON_GROUP_IDS] || []},
            [OPTIONAL_GROUPS]: {content: []},
            [SAVED_SEARCHES]: {content: recipientsByType?.[SAVED_SEARCH_IDS] || []},
            [OPTIONAL_SAVED_SEARCHES]: {content: []},
            [SITES]: {content: recipientsByType?.sites || []},
            [OPTIONAL_SITES]: {content: []},
          },
        });
      } catch (error) {
        dispatch({type: types.GET_ALL_SENDER_RECIPIENTS_FAILURE});
      }

      return;
    }

    const promises = [];
    const results = {
      [RECIPIENTS]: null,
      [OPTIONAL_RECIPIENTS]: null,
      [ADDITIONAL_RECIPIENTS]: null,

      [GROUPS]: null,
      [OPTIONAL_GROUPS]: null,
      [ADDITIONAL_GROUPS]: null,

      [SAVED_SEARCHES]: null,
      [OPTIONAL_SAVED_SEARCHES]: null,
      [ADDITIONAL_SAVED_SEARCHES]: null,

      [SITES]: null,
      [OPTIONAL_SITES]: null,
    };

    const addPromise = (recipientsData, isRequired) => {
      if (!recipientsData) return;

      const payload = buildSenderRecipientPayload(message, organizationId, isRequired);
      if (!isEmpty(recipientsData[PERSON_IDS])) {
        const key = isRequired ? RECIPIENTS : OPTIONAL_RECIPIENTS;

        promises.push(
          communicator
            .getSenderRecipientsPeople(payload)
            .then((res) => {
              results[key] = res.data;
            })
            .catch(() => {
              results[key] = {content: []};
            }),
        );
      }

      if (!isEmpty(recipientsData[PERSON_GROUP_IDS])) {
        const key = isRequired ? GROUPS : OPTIONAL_GROUPS;
        promises.push(
          communicator
            .getTemplateGroups(payload)
            .then((res) => {
              results[key] = res.data;
            })
            .catch(() => {
              results[key] = {content: []};
            }),
        );
      }

      if (!isEmpty(recipientsData[SAVED_SEARCH_IDS])) {
        const key = isRequired ? SAVED_SEARCHES : OPTIONAL_SAVED_SEARCHES;
        promises.push(
          communicator
            .getTemplateSavedSearches(payload)
            .then((res) => {
              results[key] = res.data;
            })
            .catch(() => {
              results[key] = {content: []};
            }),
        );
      }

      // Handle sites for templates
      if (!isEmpty(recipientsData[SITES])) {
        const sitesKey = isRequired ? SITES : OPTIONAL_SITES;
        const sitesPayload = {sites: recipientsData.sites};
        promises.push(
          dispatch(fetchSitesRecipients(sitesPayload))
            .then((res) => {
              results[sitesKey] = {content: res.data};
            })
            .catch(() => {
              results[sitesKey] = {content: []};
            }),
        );
      }
    };

    if (hasRequiredRecipients) {
      addPromise(senderRecipientOptions.requiredRecipients, true);
    }

    if (hasOptionalRecipients) {
      addPromise(senderRecipientOptions.optionalRecipients, false);
    }

    if (hasAdditionalRecipients) {
      addPromise(senderRecipientOptions.additionalRecipients, false);
    }

    try {
      await Promise.all(promises);

      dispatch({
        type: types.GET_ALL_SENDER_RECIPIENTS_SUCCESS,
        payload: results,
      });
    } catch (error) {
      dispatch({type: types.GET_ALL_SENDER_RECIPIENTS_FAILURE});
    }
  };
};

export const requestFetchTemplateRecipients = (payload) => ({
  type: types.SET_SHOULD_FETCH_TEMPLATE_RECIPIENTS,
  payload,
});

/**
 * Restores a set of preserved recipients back into the communicator redux state.
 * Used by HZMN_233 when the user exits the 'Yes' (include template recipients) state
 * and returns to showing the original non-template (Person Search) recipients.
 * Without this, the previous template's recipients remain in redux and the sync useEffect
 * in ComposeMessageDrawer writes them into MANAGED_RECIPIENTS instead of the original ones.
 */
export const restorePreservedRecipients = (preservedRecipients) => (dispatch) => {
  dispatch(addAllRecipients(preservedRecipients?.recipients || []));
  dispatch(addAllGroups(preservedRecipients?.groups || []));
  dispatch(addAllSavedSearches(preservedRecipients?.savedSearches || []));
  dispatch(addAllSites(preservedRecipients?.sites || []));
};

/**
 * Clears the additional recipients from the communicator redux state, e.g. once they have been moved
 * into the regular recipients pool, so they are not counted twice.
 */
export const clearAdditionalRecipients = () => (dispatch) => {
  dispatch(addAllAdditionalRecipients());
  dispatch(addAllAdditionalGroups());
  dispatch(addAllAdditionalSavedSearches());
  dispatch(addAllAdditionalSites());
};

export const deleteRecipient = (recipientId) => {
  return (dispatch, getState, {communicator}) => {
    const communicatorState = getState()?.communicator;
    const {pageable, messageId} = communicatorState;

    dispatch({type: types.DELETE_RECIPIENT_REQUEST});
    return communicator
      .deleteRecipient(recipientId)
      .then(() => {
        dispatch({
          type: types.DELETE_RECIPIENT_SUCCESS,
          payload: recipientId,
        });
        dispatch(getMessageRecipients(messageId, pageable));
        dispatch(
          notificationActions.showSuccessNotification(<FormattedMessage {...messages.deleteRecipientSuccess} />),
        );
      })
      .catch(() => {
        dispatch({type: types.DELETE_RECIPIENT_FAILURE});
        dispatch(notificationActions.showErrorNotification(<FormattedMessage {...messages.deleteRecipientFailure} />));
      });
  };
};

export const getMessage = (messageId) => {
  return (dispatch, getState, {communicator}) => {
    dispatch({type: types.GET_MESSAGE_REQUEST});

    return communicator
      .getMessage(messageId)
      .then(() => {
        dispatch({type: types.GET_MESSAGE_SUCCESS});
      })
      .catch(() => {
        dispatch({type: types.GET_MESSAGE_FAILURE});
        dispatch(notificationActions.showErrorNotification(<FormattedMessage {...messages.getMessageFailure} />));
      });
  };
};
